import { FormLayout, Remark, useDownload } from "@careinfo/layout";
import {
  AccessControl,
  ClientAccessControl,
} from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { type Table } from "@tanstack/react-table";
import moment from "moment";
import { ClipboardEvent, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/EndPoint";
import usePatchHook from "../../customHooks/usePatchHook";
import { getKey } from "../../helpers/sessionKey";
import DownloadIcon from "../../Icons/DownloadIcon";
import { createManifestValidationForm } from "../../utils/validationSchemas";
import Card from "../Reusable/Card";
import ComboboxInput from "../Reusable/ComboboxInput";
import DataTable from "../Reusable/DataTable";
import Modal from "../Reusable/Modal";
import Spinner from "../Reusable/Spinner";
import TextField from "../Reusable/TextInput";
import { getManifestItemColumn } from "../TableColumns/ManifestTableColumns";
import { getRemainningOrderColumn } from "../TableColumns/RemainingOrderTable";
import ChangeStatus from "./ChangeStatus";
import MultipleSelectField from "../Reusable/MultipleSelectField";

const EditManifest = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    gridData,
    id,
    handleAttachConsignment,
    handleManualAttachConsignment,
    handlePrint,
    isPrintLoading,
    handleStatusChange,
    defaultValue,
    isRemoved,
    handleRemove,
    isAttachLoading,
    handleSplitPrint,
    handleSplit,
    refetch,
  } = props;
  const { download } = useDownload();
  const [searchParams, setSearchParams] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });

  const { mutateAsync: setMarkAsDone, isLoading: isMarkAsDoneLoading } =
    usePatchHook({
      queryKey: [`manifest-${id}`, "manifest"],
      navigateURL: "",
    });

  const [orderId, _setOrderId] = useState(null);
  const [openStatusPopup, setOpenStatusPopup] = useState(false);
  const [statusList, setStatusList] = useState(null);
  const [initialStatusList, setInitialStatusList] = useState(null);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [freightValue, _setFreightValue] = useState(null);
  const [advanceValue, _setAdvanceValue] = useState(null);
  const [_balance, setBalance] = useState(null);
  const selectedOrders = searchParams.get("selectedOrders");
  const [showRemainingOrder, setShowRemainingOrder] = useState(false);
  const [showMarkAsDoneModal, setShowMarkAsDoneModal] = useState(false);
  const [showMarkAsUnDoneModal, setShowMarkAsUnDoneModal] = useState(false);
  const isSuperUser = getKey("is_superuser");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    setValue,
    getValues,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createManifestValidationForm),
    defaultValues: async () => {
      return {
        destination_branch: {
          name: data?.destination_branch?.name,
          id: data?.destination_branch?.id,
        },
        allowed_cities: data?.allowed_cities
          ? data?.allowed_cities?.map((item: any) => ({
              label: item?.name,
              value: item?.id,
            }))
          : [],
        note: data?.note,
      };
    },
  });

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && event.target.value?.length > 0) {
      if (!data?.is_active) {
        toast.error("Order can't be received here");
      } else if (statusData?.length > 1 && defaultValue?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          defaultValue[0]?.id,
          getValues("transit_branch"),
          setError
        );
      } else if (statusData?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          statusData[0]?.id,
          getValues("transit_branch"),
          setError
        );
      } else {
        toast.error("Please select one status first");
      }
      event.target.value = "";
    }
  };
  const checkedAllItems = searchParams.get("selectAllItems") === "1";

  const handleIndividualSelect = useCallback(
    (value: string, order: string) => {
      const clonedParams = new URLSearchParams(searchParams);
      const selectedItems = clonedParams.get("selectedItems")?.split(",");
      const selectedOrders = clonedParams.get("selectedOrders")?.split(",");
      if (
        !selectedItems ||
        !selectedItems === undefined ||
        !selectedOrders ||
        !selectedItems === undefined
      ) {
        clonedParams.set("selectedItems", value);
        clonedParams.set("selectedOrders", order);
        setSearchParams(clonedParams);
        return;
      }
      const itemIndex = selectedItems?.findIndex((item) => item === value);
      const orderIndex = selectedOrders?.findIndex((item) => item === order);
      if (itemIndex > -1 || orderIndex > -1) {
        selectedItems.splice(itemIndex, 1);
        selectedOrders.splice(itemIndex, 1);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        const newSelectedOrders = selectedOrders
          ?.map((item) => item)
          .join(",")
          .toString();
        if (newSelectedItems.length || newSelectedOrders.length) {
          clonedParams.set("selectedItems", newSelectedItems);
          clonedParams.set("selectedOrders", newSelectedOrders);
        } else {
          clonedParams.delete("selectedItems");
          clonedParams.delete("selectedOrders");
        }
        clonedParams.set("selectAllItems", "0");
      } else {
        selectedItems?.push(value);
        selectedOrders?.push(order);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        const newSelectedOrders = selectedOrders
          ?.map((item) => item)
          .join(",")
          .toString();
        clonedParams.set("selectedItems", newSelectedItems);
        clonedParams.set("selectedOrders", newSelectedOrders);
      }
      setSearchParams(clonedParams);
    },
    [searchParams, setSearchParams]
  );

  const isSelected = useCallback(
    (value: string, order: string) => {
      const selectedItems = searchParams.get("selectedItems")?.split(",");
      const selectedOrders = searchParams.get("selectedOrders")?.split(",");
      if (!selectedItems && !selectedOrders) {
        return false;
      }
      return selectedItems?.includes(value) || selectedOrders?.includes(order);
    },
    [searchParams]
  );

  const handleSelectAll = useCallback(
    (table: Table<any>) => {
      const clonedParams = new URLSearchParams(searchParams);
      const {
        options: { data: tableData },
      } = table;
      const isSelected = clonedParams.get("selectAllItems");
      if (isSelected === "1") {
        clonedParams.set("selectAllItems", "0");
        clonedParams.delete("selectedItems");
        clonedParams.delete("selectedOrders");
        setSearchParams(clonedParams);
      } else {
        const selectedItems = tableData
          ?.map((item: any) => item.id)
          .join(",")
          .toString();
        const selectedOrders = tableData
          ?.map((item: any) => item?.order?.id ?? item.id)
          .join(",")
          .toString();
        clonedParams.set("selectAllItems", "1");
        clonedParams.set("selectedItems", selectedItems);
        clonedParams.set("selectedOrders", selectedOrders);
        setSearchParams(clonedParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const manifestColumns = useCallback(
    () =>
      getManifestItemColumn({
        checkedAllItems,
        handleSelectAll,
        isSelected,
        handleIndividualSelect,
        setSelectedOrder,
        setOpenReviewModal,
      }),
    [
      checkedAllItems,
      handleSelectAll,
      isSelected,
      handleIndividualSelect,
      setSelectedOrder,
      setOpenReviewModal,
    ]
  )();

  const remaingOrderColumn = useCallback(
    () =>
      getRemainningOrderColumn({
        checkedAllItems,
        handleSelectAll,
        isSelected,
        handleIndividualSelect,
      }),
    [checkedAllItems, handleSelectAll, isSelected, handleIndividualSelect]
  )();

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error("Copy and paste is not allowed here");
  };

  const handleOrderDetailNavigate = () => {};

  const handleDownloadExcel = async () => {
    if (!selectedOrders) {
      toast.error("Please select a order to continue");
      return;
    }
    try {
      await download(
        `logisticservice/api/order/download-excel/`,
        `manifest-order-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        selectedOrders
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAsDone = () => {
    setShowMarkAsDoneModal(true);
  };

  const toggleMarkAsDone = async () => {
    const url = `${APIS.MANIFEST}${id}/`;
    let formData = {
      is_active: false,
    };
    try {
      await setMarkAsDone({ url, formData, setError });
      await refetch();
    } catch (err) {
      if (err instanceof Error) toast.error(err.message);
    } finally {
      setShowMarkAsDoneModal(false);
    }
  };

  const toggleMarkAsUnDone = async () => {
    const url = `${APIS.MANIFEST}${id}/`;
    let formData = {
      is_active: true,
    };
    try {
      await setMarkAsDone({ url, formData, setError });
      await refetch();
    } catch (err) {
      if (err instanceof Error) toast.error(err.message);
    } finally {
      setShowMarkAsUnDoneModal(false);
    }
  };

  useEffect(() => {
    let value = freightValue - advanceValue;
    setBalance((balance) => (balance != value ? value : balance));
  }, [freightValue, advanceValue]);

  useEffect(() => {
    if (Array.isArray(gridData?.results)) {
      setStatusList(gridData.results);
    }
  }, [gridData]);

  useEffect(() => {
    if (Array.isArray(statusList)) {
      const status = statusList?.filter((x: any) => x?.name === "Intermediate");
      const initialStatus = statusList?.filter(
        (x: any) => x?.name === "Initial"
      );
      setStatusData(status[0]?.status_id);
      setInitialStatusList(initialStatus[0]?.status_id);
    }
  }, [statusList]);

  const handleShowRemaningOrder = () => {
    setShowRemainingOrder((prev) => !prev);
    searchParams.delete("selectedItems");
    searchParams.delete("selectedOrders");
    setSearchParams(searchParams);
  };

  return (
    <>
      <AccessControl
        children={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={!data?.is_active}
          >
            <div className={tw`grid  grid-cols-2 gap-x-4 px-4 `}>
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Branch"}
                setValue={setValue}
                props={{
                  is_active: true,
                }}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <MultipleSelectField
                label={"Allowed Cities"}
                queryKey={"allowed-city"}
                register={register("allowed_cities")}
                watch={watch}
                setValue={setValue}
                url={`${APIS.LOCATION}flat-location/`}
                errors={errors.allowed_cities}
                value={
                  data?.allowed_cities
                    ? data?.allowed_cities?.map((item: any) => ({
                        label: item?.name,
                        value: item?.id,
                      }))
                    : []
                }
                props={{
                  level: 3,
                  is_active: true,
                }}
              />
            </div>
            <div className={tw`mx-4`}>
              <TextField
                label={"Note"}
                register={register(`note`, { disabled: !data?.is_active })}
                errors={errors?.note}
              />
            </div>
          </FormLayout>
        }
        allowedPermissions={["can_editupdate_manifest"]}
      />
      <Card title="Barcode Scanner">
        <>
          <div className={tw`flex flex-col  items-end w-full`}>
            <div
              className={tw`flex flex-row gap-x-4 items-center w-full justify-end`}
            >
              <ChangeStatus
                gridData={statusData}
                defaultValue={defaultValue}
                handleStatusChange={handleStatusChange}
              />
            </div>

            <div className={tw`grid grid-cols-1 w-full`}>
              <div>
                <input
                  type="text"
                  {...register("tracking_number")}
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  onPaste={preventCopyPaste}
                  onKeyDown={handleKeyDown}
                />
                {errors?.tracking_number && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.tracking_number?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      </Card>
      <>
        {/* {!isGettingRemainingOrder && ( */}
        <button
          className={tw`py-2 w-36 text-center  bg-primary-light my-2 rounded text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
          onClick={() => handleShowRemaningOrder()}
        >
          <p>{showRemainingOrder ? "Manifest Items" : "Remaining Order"}</p>
        </button>
        {/* )} */}
      </>

      {!showRemainingOrder ? (
        <DataTable
          url={`${APIS.MANIFEST_ITEMS}`}
          queryKey={"manifest-items"}
          columns={manifestColumns}
          params={{ manifest: data?.id, order_by: "-modified" }}
          tableName={"manifest-items"}
          handleDetailNavigate={handleOrderDetailNavigate}
          actionMenu={
            <div className={tw`w-full text-right flex items-center`}>
              <button
                className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={handleDownloadExcel}
              >
                <DownloadIcon color={"var(--primary)"} />
                Download
              </button>

              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handleSplitPrint();
                }}
              >
                {isPrintLoading ? <Spinner /> : "Split Print"}
              </button>
              <ClientAccessControl
                allowedPermissions={["can_view_split_button"]}
              >
                <button
                  className={tw`p-1 w-14 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                  disabled={isPrintLoading}
                  onClick={() => {
                    data?.is_active
                      ? toast.error("Please Mark as done first")
                      : handleSplit(setError);
                  }}
                >
                  {isPrintLoading ? <Spinner /> : "Split"}
                </button>
              </ClientAccessControl>
              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5 rounded-sm border border-primary mr-2  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                  onClick={handleMarkAsDone}
                >
                  Mark As Done
                </button>
              ) : isSuperUser === "true" ? (
                <button
                  className={tw`p-1 px-5 rounded-sm border border-primary mr-2  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                  onClick={() => setShowMarkAsUnDoneModal(true)}
                >
                  Revert
                </button>
              ) : null}
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                disabled={isPrintLoading}
                onClick={() => {
                  data?.is_active
                    ? toast.error("Please Mark as done first")
                    : handlePrint(getValues("transit_branch"));
                }}
              >
                {isPrintLoading ? <Spinner /> : "Print"}
              </button>
              {data?.is_active ? (
                <button
                  className={tw`p-1 px-5  rounded-sm border border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                  disabled={isRemoved}
                  onClick={() => handleRemove()}
                >
                  {isRemoved ? <Spinner /> : "Remove"}
                </button>
              ) : (
                ""
              )}
            </div>
          }
        />
      ) : (
        <DataTable
          url={`${APIS.LOGISTIC_ORDER}`}
          queryKey={"remaining-order"}
          columns={remaingOrderColumn}
          params={{
            // manifest_id__isnull: true,
            to_branch: watch("to_branch"),
            delivery_status__in: initialStatusList
              ?.map((item) => item?.id)
              .join(","),
          }}
          tableName={"remaining-order"}
          handleDetailNavigate={handleOrderDetailNavigate}
          actionMenu={
            <ClientAccessControl
              allowedPermissions={["can_manually_attach_order"]}
            >
              <div className={tw`w-full flex gap-x-2 items-center`}>
                <div className={tw`w-52`}>
                  <ComboboxInput
                    url={APIS.BRANCH}
                    queryKey={"branch"}
                    register={register("to_branch")}
                    errors={errors?.to_branch}
                    name={"to_branch"}
                    // label={"Branch"}
                    setValue={setValue}
                    props={{
                      is_active: true,
                    }}
                    placeholder={"Choose branch"}
                    getValues={getValues}
                    clearErrors={clearErrors}
                  />
                </div>
                {data?.is_active ? (
                  <button
                    className={tw`p-1 px-5  rounded-sm border border-primary  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                    disabled={isAttachLoading}
                    onClick={() => {
                      handleManualAttachConsignment(
                        statusData[0]?.id,
                        setError
                      );
                    }}
                  >
                    {isAttachLoading ? <Spinner /> : "Attach"}
                  </button>
                ) : null}
              </div>
            </ClientAccessControl>
          }
        />
      )}

      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
      >
        <div className={tw`w-[600px]`}>
          <Remark selectedOrder={selectedOrder} />
        </div>
      </Modal>
      <Modal
        isOpen={showMarkAsDoneModal}
        setIsOpen={setShowMarkAsDoneModal}
        showTitle={false}
      >
        <div className={tw`grid place-items-center w-fit`}>
          <div className={tw`space-y-3`}>
            <h2 className={tw`text-xl font-bold text-center`}>Are you sure?</h2>
            <div className={tw`flex items-center gap-5 justify-end`}>
              <button
                className={tw`bg-gray-200 rounded-lg px-2 py-2 focus:outline-none`}
                onClick={() => setShowMarkAsDoneModal(false)}
              >
                Cancel
              </button>
              <button
                onClick={toggleMarkAsDone}
                className={tw`px-2 py-2 rounded-lg bg-primary  text-white disabled:bg-primary-light`}
                disabled={isMarkAsDoneLoading}
              >
                <p>Mark As done</p>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showMarkAsUnDoneModal}
        setIsOpen={setShowMarkAsUnDoneModal}
        showTitle={false}
      >
        <div className={tw`grid place-items-center w-fit`}>
          <div className={tw`space-y-3`}>
            <h2 className={tw`text-xl font-bold text-center`}>Are you sure?</h2>
            <div className={tw`flex items-center gap-5 justify-end`}>
              <button
                className={tw`bg-gray-200 rounded-lg px-2 py-2 focus:outline-none`}
                onClick={() => setShowMarkAsUnDoneModal(false)}
              >
                Cancel
              </button>
              <button
                onClick={toggleMarkAsUnDone}
                className={tw`px-6 py-2 rounded-lg bg-primary  text-white disabled:bg-primary-light`}
                disabled={isMarkAsDoneLoading}
              >
                <p>Yes</p>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={openStatusPopup}
        setIsOpen={setOpenStatusPopup}
        title={"Change Status To"}
      >
        <ChangeStatus
          handleAttachConsignment={handleAttachConsignment}
          openStatusPopup={openStatusPopup}
          setOpenStatusPopup={setOpenStatusPopup}
          orderId={orderId}
        />
      </Modal>
    </>
  );
};

export default EditManifest;
